import CustomDatePicker from "@/components/CustomDatePicker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useBookingByShortId } from "@/hooks/data/useBookingByShortId";
import { useBookings } from "@/hooks/useBookings";
import { useCreateIncome, useUpdateIncome } from "@/hooks/admin/shared/useIncomeMutations";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Check, ChevronsUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface IncomeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingIncome?: any;
  defaultValues?: Partial<IncomeFormData>;
}

const incomeSchema = z.object({
  booking_id: z.string().min(1, "Reservación es requerida"),
  income_type: z.enum([
    "Hospedaje",
    "Early Checkin",
    "Late Checkout",
    "Limpieza adicional",
    "Servicios",
    "Otro",
  ]),
  payment_type: z.enum(["Transferencia", "Efectivo"]),
  gross_payment: z.number().min(0, "Pago bruto debe ser mayor a 0"),
  iva: z.number().optional(),
  ret_iva: z.number().optional(),
  ret_isr: z.number().optional(),
  comission: z.number().optional(),
  payout: z.number().min(0, "Pago recibido es requerido"),
  payout_date: z.date(),
  fiscal_invoice: z.boolean(),
});

type IncomeFormData = z.infer<typeof incomeSchema>;

const IncomeFormModal: React.FC<IncomeFormModalProps> = ({
  isOpen,
  onClose,
  editingIncome,
  defaultValues,
}) => {
  const [bookingSearchOpen, setBookingSearchOpen] = useState(false);
  const [bookingSearchValue, setBookingSearchValue] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState("");

  const { data: bookings } = useBookings();
  const { data: selectedBooking } = useBookingByShortId(selectedBookingId);
  const createIncome = useCreateIncome();
  const updateIncome = useUpdateIncome();

  const form = useForm<IncomeFormData>({
    resolver: zodResolver(incomeSchema),
    defaultValues: {
      booking_id: defaultValues?.booking_id || "",
      income_type: defaultValues?.income_type || "Hospedaje",
      payment_type: defaultValues?.payment_type || "Transferencia",
      gross_payment: defaultValues?.gross_payment || 0,
      iva: defaultValues?.iva || 0,
      ret_iva: defaultValues?.ret_iva || 0,
      ret_isr: defaultValues?.ret_isr || 0,
      comission: defaultValues?.comission || 0,
      payout: defaultValues?.payout || 0,
      payout_date: new Date(),
      fiscal_invoice: false,
    },
  });

  const watchedIncomeType = form.watch("income_type");

  useEffect(() => {
    if (editingIncome && isOpen) {
      setSelectedBookingId(editingIncome.booking_id);
      form.reset({
        booking_id: editingIncome.booking_id,
        income_type: editingIncome.income_type,
        payment_type: editingIncome.payment_type,
        gross_payment: editingIncome.gross_payment,
        iva: editingIncome.iva || 0,
        ret_iva: editingIncome.ret_iva || 0,
        ret_isr: editingIncome.ret_isr || 0,
        comission: editingIncome.comission || 0,
        payout: editingIncome.payout,
        payout_date: new Date(editingIncome.payout_date),
        fiscal_invoice: editingIncome.fiscal_invoice,
      });
    } else if (!editingIncome && isOpen && defaultValues) {
      if (defaultValues.booking_id) {
        setSelectedBookingId(defaultValues.booking_id);
      }
      form.reset({
        booking_id: defaultValues.booking_id || "",
        income_type: defaultValues.income_type || "Hospedaje",
        payment_type: defaultValues.payment_type || "Transferencia",
        gross_payment: defaultValues.gross_payment || 0,
        iva: defaultValues.iva || 0,
        ret_iva: defaultValues.ret_iva || 0,
        ret_isr: defaultValues.ret_isr || 0,
        comission: defaultValues.comission || 0,
        payout: defaultValues.payout || 0,
        payout_date: defaultValues.payout_date || new Date(),
        fiscal_invoice: defaultValues.fiscal_invoice || false,
      });
    } else if (!editingIncome && isOpen) {
      setSelectedBookingId("");
      form.reset();
    }
  }, [editingIncome, isOpen, defaultValues, form]);

  const filteredBookings =
    bookings?.filter((booking) =>
      booking.booking_id
        .toLowerCase()
        .includes(bookingSearchValue.toLowerCase()),
    ) || [];

  const onSubmit = async (data: IncomeFormData) => {
    try {
      const payload = {
        booking_id: data.booking_id,
        income_type: data.income_type,
        payment_type: data.payment_type,
        gross_payment: data.gross_payment,
        iva: data.iva || 0,
        ret_iva: data.ret_iva || 0,
        ret_isr: data.ret_isr || 0,
        comission: data.comission || 0,
        payout: data.payout,
        payout_date: format(data.payout_date, "yyyy-MM-dd"),
        fiscal_invoice: data.fiscal_invoice,
      };

      if (editingIncome) {
        await updateIncome.mutateAsync({ id: editingIncome.id, data: payload });
      } else {
        await createIncome.mutateAsync(payload);
      }

      onClose();
    } catch (error) {
      console.error("Error saving income:", error);
    }
  };

  const handleBookingSelect = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    form.setValue("booking_id", bookingId);
    setBookingSearchOpen(false);
  };

  const isHospedaje = watchedIncomeType === "Hospedaje";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingIncome ? "Editar Ingreso" : "Crear Nuevo Ingreso"}
          </DialogTitle>

          <DialogDescription>
            {editingIncome
              ? "Modifica los datos del ingreso"
              : "Completa los datos para crear un nuevo ingreso"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form 
            onSubmit={form.handleSubmit(onSubmit)} 
            className="space-y-4"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}
          >
            <FormField
              control={form.control}
              name="booking_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Reservación</FormLabel>

                  <Popover
                    open={bookingSearchOpen}
                    onOpenChange={setBookingSearchOpen}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            "justify-between",
                            !field.value && "text-muted-foreground",
                          )}
                        >
                          {field.value || "Seleccionar reservación..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>

                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput
                          placeholder="Buscar reservación..."
                          value={bookingSearchValue}
                          onValueChange={setBookingSearchValue}
                        />

                        <CommandList>
                          <CommandEmpty>
                            No se encontraron reservaciones.
                          </CommandEmpty>

                          <CommandGroup>
                            {filteredBookings.map((booking) => (
                              <CommandItem
                                key={booking.id}
                                value={booking.booking_id}
                                onSelect={() =>
                                  handleBookingSelect(booking.booking_id)
                                }
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    field.value === booking.booking_id
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <div>
                                  <div className="font-medium">
                                    {booking.booking_id}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {booking.guest_name} •{" "}
                                    {format(
                                      new Date(booking.check_in),
                                      "dd/MM/yy",
                                    )}{" "}
                                    -{" "}
                                    {format(
                                      new Date(booking.check_out),
                                      "dd/MM/yy",
                                    )}
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {selectedBooking && (
                    <div className="p-2 bg-muted rounded-md text-sm">
                      <div className="font-medium">
                        {selectedBooking.guest_name}
                      </div>

                      <div className="text-muted-foreground">
                        {format(
                          new Date(selectedBooking.check_in),
                          "dd/MM/yy",
                        )}{" "}
                        -{" "}
                        {format(
                          new Date(selectedBooking.check_out),
                          "dd/MM/yy",
                        )}
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="gross_payment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pago Bruto</FormLabel>

                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="payment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Pago</FormLabel>

                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent>
                        <SelectItem value="Transferencia">
                          Transferencia
                        </SelectItem>

                        <SelectItem value="Efectivo">Efectivo</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fiscal_invoice"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>

                    <div className="space-y-1 leading-none">
                      <FormLabel>Requiere Factura</FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="income_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Ingreso</FormLabel>

                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent>
                        <SelectItem value="Hospedaje">Hospedaje</SelectItem>

                        <SelectItem value="Early Checkin">
                          Early Checkin
                        </SelectItem>

                        <SelectItem value="Late Checkout">
                          Late Checkout
                        </SelectItem>

                        <SelectItem value="Limpieza adicional">
                          Limpieza adicional
                        </SelectItem>

                        <SelectItem value="Servicios">Servicios</SelectItem>

                        <SelectItem value="Otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="iva"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IVA</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isHospedaje && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="ret_iva"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Retención IVA</FormLabel>

                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value) || 0)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ret_isr"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Retención ISR</FormLabel>

                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value) || 0)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="comission"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Comisión</FormLabel>

                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="payout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pago Recibido</FormLabel>

                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="payout_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Pago</FormLabel>

                    <FormControl>
                      <CustomDatePicker
                        selected={field.value}
                        onSelect={field.onChange}
                        placeholder="Seleccionar fecha"
                        className="w-full h-fit"
                        isAdminContext={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>

              <Button
                type="submit"
                disabled={createIncome.isPending || updateIncome.isPending}
                className="bg-brand-primary hover:bg-brand-primary-hover"
              >
                {editingIncome ? "Actualizar" : "Crear"} Ingreso
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default IncomeFormModal;
